/**
 * lib/game-routing — per-route URL для ИГРОВЫХ экранов (ADR-0020 Фаза 3).
 *
 * ЗАЧЕМ / WHY THIS EXISTS: ADR-0020 Фаза 3 (owner-reversal 2026-06-16 — делаем
 * СЕЙЧАС, не пред-релиз): каждый игровой экран получает свой адрес (`/play`,
 * `/daily`, `/leaderboard`, `/profile`, `/companions`). До этого SPA
 * жил на одном URL (`/`), фаза в Zustand → адресная строка не менялась при
 * навигации, экраны не шарились, краулер не видел отдельных страниц. Этот модуль —
 * ЕДИНЫЙ ствол маппинга URL↔phase (модель ствол→ветки): один источник правды
 * «какой путь у какого экрана», переиспользуемый и app.ts-роутером (pushState на
 * навигации, deep-link на загрузке, popstate Back/Forward), и SSG-зеркалом
 * (краулер видит те же пути).
 *
 * АДРЕСА ORIGIN-RELATIVE (owner-directive 2026-06-16 verbatim: «делать локальный
 * адрес, а не colorlines98.online»): все пути тут — относительные (`/play`),
 * работают на :5173 и по LAN. Боевой домен — ТОЛЬКО в краулерских мета
 * (canonical/OG/sitemap) через PUBLIC_BASE_URL, не во внутренней навигации.
 *
 * SCOPE: маппинг phase↔path (стабильные nav-направления), classify boot-path
 *   игрового экрана, applyGameRoute (вызвать store-действие фазы по пути).
 * NOT IN SCOPE: контент-роутинг (lib/content-routing), `/r/<id>` deep-link
 *   (остаётся в app.ts parseReplayShortIdFromPath — server-authoritative seed),
 *   монтаж экранов (app.ts mountPhase), drawer/настройки (оверлей-утилита, не путь).
 *
 * Транзиентные фазы (board / result / replay / settings) СВОЕГО пути не имеют —
 * это пост-/внутри-игровые состояния поверх стабильного направления (board живёт
 * под /play или /daily по context.kind; result/replay — пост-игровые). У них нет
 * отдельной shareable-страницы (кроме `/r/<id>`, который свой deep-link). pathForPhase
 * для них возвращает путь их «родительского» направления (board) либо null
 * (result/replay/settings — адрес не трогаем), чтобы НЕ плодить неиндексируемые URL.
 *
 * Cross-references: ADR-0020 Фаза 3 + §«Anti-shadow /daily» (I-2); store Phase;
 *   lib/content-routing (контентные пути — соседний ствол).
 */

import { contentPath } from '@lines98/shared/config/content-routes';
import { type AppRoutePath } from '@lines98/shared/config/app-routes';

import type { Phase } from '../store/game-store.js';
import { useGameStore } from '../store/game-store.js';

/**
 * Стабильные ИГРОВЫЕ nav-направления → origin-relative путь. ЕДИНЫЙ источник: и pushState
 * (app.ts), и deep-link, и SSG-зеркало читают этот словарь. Совпадает с пунктами
 * рейла (`realNavItems` в app.ts) — рейл и URL не дрейфуют.
 *
 * СТАРТОВАЯ-ЛЕНДИНГ (`start`) НАМЕРЕННО НЕ ЗДЕСЬ (fix/i18n-unify-homepage, owner-решение
 * 2026-06-19). Её URL НЕ статический `/` — он ЯЗЫКО-ЗАВИСИМ по URL-модели B (en→`/`,
 * ru→`/ru/`): единая ИНДЕКСИРУЕМАЯ главная, следующая языку рейла. Статический `start:'/'`
 * спорил бы с `/ru/` (ru-лендинг) и с контентным парсером модели B. Путь стартовой даёт
 * `pathForPhase('start')` через `contentPath(locale,'')` (единый источник модели B); boot/
 * popstate в app.ts распознают «голый лендинг» (slug='') ДО игрового классификатора.
 * Поэтому `classifyGamePath('/')` НЕ матчит стартовую (это не игровой route, а главная).
 */
export const GAME_ROUTE_PATHS = {
  play: '/play',
  daily: '/daily',
  leaderboard: '/leaderboard',
  // ВНУТРЕННИЙ id `gnomes` (route-key / phase / store-действие) НЕ переименован —
  // это код-термин. Переименован ТОЛЬКО видимый URL: `/gnomes` → `/companions`
  // (owner-decision 2026-06-21). Старый `/gnomes` 301-редиректит на `/companions`
  // (shared `LEGACY_APP_ROUTE_REDIRECTS` / `legacyAppRouteRedirect`; app.ts boot+popstate
  // dev replaceState, прод-Caddy 301). en-подпись «Helpers»→«Companions» (каталог i18n).
  // NB: `workshop` тут НАМЕРЕННО НЕТ — он retired (workshop-animals-removal.md O-3),
  // живёт только в APP_ROUTE_PATHS как noindex-заглушка + isRetiredGamePath.
  gnomes: '/companions',
  profile: '/profile',
  // «Архив игр» — личный каталог реплеев (ADR-0023). Стабильное nav-направление:
  // deep-link + popstate монтируют экран Архива (showBook). ВНУТРЕННИЙ id `book`
  // (route-key/phase/store-действие/CSS `.book-*`) НЕ переименован — это код-термин.
  // Переименован ТОЛЬКО видимый URL: `/book`→`/archive` (owner «Маршрут — ФИНАЛ»
  // 2026-06-24). Старый `/book` 301-редиректит на `/archive` (shared
  // `LEGACY_APP_ROUTE_REDIRECTS`; app.ts boot+popstate replaceState, прод-Caddy 301),
  // как `/gnomes`→`/companions`.
  book: '/archive',
  // Страница «Поддержать» (крипто-донат L0, ADR-0024 addendum). app-поверхность
  // (интерактивная, не индексируемый контент) → noindex через APP_ROUTE_PATHS.
  support: '/support',
} as const;

/** Идентификатор стабильного игрового направления (ключ GAME_ROUTE_PATHS). */
export type GameRouteId = keyof typeof GAME_ROUTE_PATHS;

/**
 * RETIRED игровые пути — больше НЕ резолвятся в монтаж экрана, дают graceful fallback
 * на дефолт-поверхность (workshop-animals-removal.md O-3 / fe-workshop-disabled AC-6).
 *
 * `/workshop` (мастерская «животных») снят: пункт рейла теперь disabled (серый, под
 * «Помощниками»), экран зверей удалён. Путь оставлен в `APP_ROUTE_PATHS` как noindex-
 * заглушка (robots/Caddy/SPA-meta его noindex'ят, O-3), но прямой заход / popstate
 * НЕ монтирует экран зверей и НЕ даёт белый экран — `applyGameRoute`-роутер app.ts
 * редиректит на дефолт (modeSelect/`/play`). НЕ классифицируется `classifyGamePath`
 * (он матчит только живые направления) — отдельный предикат `isRetiredGamePath`.
 */
export const RETIRED_GAME_PATHS: readonly AppRoutePath[] = ['/workshop'];

/**
 * `true`, если путь — снятый игровой route (см. `RETIRED_GAME_PATHS`). Терпим к
 * замыкающему «/». app.ts (boot + popstate) на такой путь делает graceful fallback на
 * дефолт-поверхность (а не монтаж экрана / белый экран / контент-404).
 */
export function isRetiredGamePath(pathname: string): boolean {
  const clean = pathname.replace(/\/+$/, '') || '/';
  return (RETIRED_GAME_PATHS as readonly string[]).includes(clean);
}

/**
 * Compile-time guard: каждый путь GAME_ROUTE_PATHS обязан быть в shared-каноне
 * `APP_ROUTE_PATHS` (тот же список, что noindex'ится в robots.txt / SPA-meta / Caddy,
 * SEO-хардинг 2026-06-19). Если здесь заведут игровой путь, отсутствующий в shared-
 * списке (или наоборот переименуют), TS упадёт ЗДЕСЬ на компиляции — robots и
 * клиент не разъедутся (единый источник app-путей = APP_ROUTE_PATHS). Чисто
 * compile-time (`void`, без рантайм-стоимости).
 */
const _gameRoutesAreAppRoutes: readonly AppRoutePath[] = Object.values(GAME_ROUTE_PATHS);
void _gameRoutesAreAppRoutes;

/** Обратный индекс path → routeId (для разбора boot/popstate-пути). */
const PATH_TO_ROUTE: ReadonlyMap<string, GameRouteId> = new Map(
  (Object.entries(GAME_ROUTE_PATHS) as [GameRouteId, string][]).map(([id, p]) => [p, id]),
);

/**
 * Путь, который должен стоять в адресной строке для данной фазы.
 *   - стабильные направления (modeSelect/daily/leaderboard/gnomes/profile)
 *     → свой путь;
 *   - board → /daily если контекст daily, иначе /play (board живёт ПОД направлением);
 *   - транзиентные (result / replay / settings / content) → null (адрес не трогаем —
 *     остаётся путь родительского направления; своей shareable-страницы у них нет.
 *     content имеет СВОЙ путь, но управляется lib/content-routing, не отсюда).
 * `/r/<id>` (replay deep-link) обрабатывается ОТДЕЛЬНО в app.ts (server-authoritative).
 */
export function pathForPhase(phase: Phase): string | null {
  switch (phase) {
    case 'start':
      // ЕДИНАЯ ИНДЕКСИРУЕМАЯ ГЛАВНАЯ (fix/i18n-unify-homepage): URL стартовой-лендинга
      // ЯЗЫКО-ЗАВИСИМ по URL-модели B (en→`/`, ru→`/ru/`) — следует глобальному `locale`
      // рейла. Через ЕДИНЫЙ источник модели B `contentPath` (тот же, что контент/SSG),
      // НЕ статический `/`. app.ts на смену locale на стартовой пушит этот путь.
      return contentPath(useGameStore.getState().locale, '');
    case 'modeSelect':
      return GAME_ROUTE_PATHS.play;
    case 'daily':
      return GAME_ROUTE_PATHS.daily;
    case 'leaderboard':
      return GAME_ROUTE_PATHS.leaderboard;
    case 'gnomes':
      return GAME_ROUTE_PATHS.gnomes;
    case 'profile':
      return GAME_ROUTE_PATHS.profile;
    case 'book':
      return GAME_ROUTE_PATHS.book;
    case 'support':
      return GAME_ROUTE_PATHS.support;
    case 'board': {
      // board — под направлением: daily-партия живёт под /daily, casual под /play.
      const kind = useGameStore.getState().context.kind;
      return kind === 'daily' ? GAME_ROUTE_PATHS.daily : GAME_ROUTE_PATHS.play;
    }
    // Транзиентные пост-/оверлей-состояния — адрес не меняем (нет своего URL).
    // content — управляется lib/content-routing (свой pushState), не game-routing.
    case 'result':
    case 'replay':
    case 'settings':
    case 'content':
      return null;
  }
}

/**
 * Разобрать boot/popstate pathname в игровое направление. Возвращает routeId или
 * null (не игровой путь — caller отдаёт контенту/реплею/oauth/дефолту). Терпим к
 * замыкающему «/» (History-навигация / ручной ввод могут его добавить).
 */
export function classifyGamePath(pathname: string): GameRouteId | null {
  const clean = pathname.replace(/\/+$/, '') || '/';
  return PATH_TO_ROUTE.get(clean) ?? null;
}

/**
 * Применить игровое направление = вызвать соответствующее store-действие фазы.
 * Используется на deep-link (boot) И на popstate (Back/Forward). НЕ делает
 * pushState (это делает app.ts на forward-навигации) — здесь только смена фазы.
 *
 * I-2 anti-shadow (`/daily`): `showDaily()` ведёт на хаб доски дня (ритуал +
 * отсчёт + CTA), НЕ запускает сегодняшнюю раздачу/seed — daily-партия стартует
 * только явным действием игрока (как и было). Deep-link `/daily` → хаб, не партия.
 */
export function applyGameRoute(id: GameRouteId): void {
  const store = useGameStore.getState();
  switch (id) {
    case 'play':
      store.showModeSelect();
      return;
    case 'daily':
      store.showDaily();
      return;
    case 'leaderboard':
      store.showLeaderboard();
      return;
    case 'gnomes':
      store.showGnomes();
      return;
    case 'profile':
      store.showProfile();
      return;
    case 'book':
      store.showBook();
      return;
    case 'support':
      store.showSupport();
      return;
  }
}
